import moment from 'moment';
import { getLocalStorage, setLocalStorage } from '.';
import { location, searches } from './localStorage';
import { isNotExpired } from './dates';
import { getLocationInfoForCurrentUser } from './ipstack';

export const getCountryViewport = (countryCode) => {
  if (countryCode && countryCode.toLowerCase() === 'au') {
    return {
      city: 'Australia',
      locationName: 'Australia',
      viewportNorthEastLat: -0.69,
      viewportNorthEastLong: 166.74,
      viewportSouthWestLat: -51.66,
      viewportSouthWestLong: 100.09,
    };
  }
  if (countryCode && countryCode.toLowerCase() === 'us') {
    return {
      city: 'USA',
      locationName: 'USA',
      viewportNorthEastLat: 49.38,
      viewportNorthEastLong: -66.94,
      viewportSouthWestLat: 25.82,
      viewportSouthWestLong: -124.39,
    };
  }
  if (countryCode && countryCode.toLowerCase() === 'gb') {
    return {
      city: 'GB',
      locationName: 'United Kingdom',
      viewportNorthEastLat: 60.9,
      viewportNorthEastLong: 2.1,
      viewportSouthWestLat: 49.8,
      viewportSouthWestLong: -8.9,
    };
  }
  return undefined;
};

export const getGeoLocation = async () => {
  if (__CLIENT__) {
    const lastSearch = JSON.parse(getLocalStorage(searches.last.key));
    if (lastSearch && isNotExpired(lastSearch.expiresAt)) {
      const formValues = lastSearch.formValues;
      return formValues.locationLat && formValues.locationLong
        ? {
            city: formValues.locationName,
            latitude: formValues.locationLat,
            longitude: formValues.locationLong,
          }
        : {
            city: formValues.locationName,
            viewportNorthEastLat: formValues.viewportNorthEastLat,
            viewportNorthEastLong: formValues.viewportNorthEastLong,
            viewportSouthWestLat: formValues.viewportSouthWestLat,
            viewportSouthWestLong: formValues.viewportSouthWestLong,
          };
    }

    const recentLocation = JSON.parse(getLocalStorage(location.last.key));
    if (recentLocation && isNotExpired(recentLocation.expiresAt)) {
      return recentLocation.location;
    }

    // If no recent searches or recent location lookups, then use the user's current location.
    const locationInfo = await getLocationInfoForCurrentUser();
    if (locationInfo.city && locationInfo.latitude && locationInfo.longitude) {
      const currentLocation = {
        city: locationInfo.city,
        latitude: locationInfo.latitude.toString(),
        longitude: locationInfo.longitude.toString(),
      };

      if (locationInfo.country && locationInfo.countrycode) {
        currentLocation.country = locationInfo.country;
        currentLocation.countrycode = locationInfo.countrycode;
      }

      setLocalStorage(
        location.last.key,
        JSON.stringify({
          expiresAt: moment.utc().add(12, 'hours'),
          location: currentLocation,
        }),
      );
      return currentLocation;
    }
  }
  return {};
};

export const getLocalQuery = (query, geoLocation = {}) => ({
  ...query,
  ...(geoLocation.city &&
    (geoLocation.latitude
      ? {
          locationName: geoLocation.city,
          locationLat: geoLocation.latitude,
          locationLong: geoLocation.longitude,
          locationSearchRadius: geoLocation.locationSearchRadius || 100,
        }
      : {
          city: geoLocation.locationName,
          viewportNorthEastLat: geoLocation.viewportNorthEastLat,
          viewportNorthEastLong: geoLocation.viewportNorthEastLong,
          viewportSouthWestLat: geoLocation.viewportSouthWestLat,
          viewportSouthWestLong: geoLocation.viewportSouthWestLong,
        })),
});

const centerPoints = {
  au: {
    lat: -30,
    long: 145,
    zoom: 4,
  },
  us: {
    lat: 40,
    long: -95,
    zoom: 4,
  },
  gb: {
    lat: 54.5074,
    long: -3.6,
    zoom: 6,
  },
};

export function getCountryDefaultZoom(countryCode = '') {
  const code = countryCode.toLowerCase();
  const country = centerPoints[code] || centerPoints.gb;
  return country.zoom;
}

export function getCountryCenterPoint(countryCode = '') {
  const code = countryCode.toLowerCase();
  const country = centerPoints[code] || centerPoints.gb;
  const { lat, long } = country;
  return { lat, long };
}

/*
 @param API {googleAPI}
 @param coords {latitude, longitude}
 @returns {Promise}
 */
export function getGooglePlaceFromCoordinates(API, coords) {
  return new Promise((resolve) => {
    let googleAPI = API;

    if (__CLIENT__) {
      if (!googleAPI && typeof window !== 'undefined') {
        googleAPI = window.google;
      }
    }

    if (!googleAPI || !coords) resolve(null);

    let { latitude: lat, longitude: lng } = coords;

    /* Google Place geolocation lng function for San Francisco returns -122.41941550000001
     but... they can't decode that value correctly back to SF with their Geocoder.
     rounding the value fixes the issue */
    lat = Number(Number(lat).toFixed(4));
    lng = Number(Number(lng).toFixed(4));

    const geocoder = new googleAPI.maps.Geocoder();

    let googlePlaceLocation = null;

    // Use Google Geocoder to resolve coordinates to Google place
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK') {
        let localityName;
        const firstResult = results[0];

        if (firstResult && firstResult.address_components) {
          const localityObj = firstResult.address_components.filter(
            (a) =>
              a.types.includes('locality') || a.types.includes('postal_town'),
          )[0];

          if (localityObj) {
            localityName = localityObj.long_name || localityObj.short_name;
          } else {
            localityName = 'Current location';
          }

          googlePlaceLocation = { ...firstResult, name: localityName };
        }
        resolve(googlePlaceLocation);
      } else {
        resolve(null);
      }
    });
  });
}
